import { can, type Capability } from '../../lib/api'
import { onScreen } from '../../lib/screens'

// The entries of Settings, in one place, for the frame that draws them and for
// the routes that open them (11 Oct 2026).
//
// # WHY ONE LIST
//
// Six of these entries are screens with addresses of their own (the approval
// matrix, people, custom fields, the onboarding steps, due diligence and
// exception authority) and eleven are panes of the Settings screen. Pressing a
// screen's entry used to replace the whole page with it: the list was gone and
// four of the six had no way back. A person could not tell which entries stay
// and which leave, because the list marked them differently and nothing said
// why. Now every entry opens inside one frame (SettingsFrame.tsx) and Settings
// never leaves the screen, so the bar the frame draws, the screens App routes
// to it and the persona filter all read this one table.
//
// # WHAT AN ENTRY IS
//
// `screen` is set for an entry that has a screen of its own, and is that
// screen's route name in App.tsx; its address is unchanged (mail links, My work
// rows and the persona spec point at them). A pane has no screen and is reached
// as #/settings?t=<key>, or #/settings/<key>, the shape the workflows catalogue
// links with.

export type Entry = {
  key: string
  label: string
  // One line on the Settings index, at most eight words (plan 3.9), and what
  // the index's search reads besides the label.
  about: string
  group: string
  cap: Capability
  // The route name of an entry that is a screen of its own.
  screen?: string
  // The address of that screen, where it is not simply #/<screen>.
  address?: string
}

export const ENTRIES: Entry[] = [
  // Organisation
  { key: 'organisation', label: 'Organisation', about: 'Who this workspace is: name and details', group: 'Organisation', cap: 'config.read' },
  { key: 'onboarding', label: 'Onboarding steps', about: 'The steps a new vendor goes through', group: 'Organisation', cap: 'config.read', screen: 'onboarding-process' },
  { key: 'approvals', label: 'Who approves', about: 'Who signs a vendor into the register', group: 'Organisation', cap: 'config.read', screen: 'approval-matrix' },
  // The due diligence exception workflow (section 4 of the product officer's
  // observations): its parameters, and who may approve at each severity.
  // Labelled "Exception parameters" since release 3.3 (C8): "Due diligence"
  // also named the questionnaire stage. The key and the address are kept.
  { key: 'due-diligence', label: 'Exception parameters', about: 'What an exception is raised under', group: 'Organisation', cap: 'config.read', screen: 'due-diligence', address: '#/settings/due-diligence' },
  { key: 'exception-authority', label: 'Who can accept risks', about: 'Who approves a risk acceptance at each severity', group: 'Organisation', cap: 'config.read', screen: 'exception-authority', address: '#/settings/exception-authority' },
  // The tier defaults, moved here from the Reassessment screen when that
  // became a list of vendors (observation 3.14).
  { key: 'reassessment', label: 'Reassessment', about: 'Questionnaire, model and frequency for each tier', group: 'Organisation', cap: 'config.read' },
  // Who gets which questionnaire, by group, type and tier, in front of the
  // tier defaults above (release 3.4, M7).
  { key: 'baselines', label: 'Baselines', about: 'Who gets which questionnaire, by group', group: 'Organisation', cap: 'config.read' },
  // Every process the programme runs, drawn from its rules (T10, 21 September).
  { key: 'workflows', label: 'Workflows', about: 'Every process the programme runs, as diagrams', group: 'Organisation', cap: 'config.read' },
  // Its table scrolls sideways inside its box below 853. It also breaks "Edit"
  // across lines on four or five buttons at every width on main (a separate
  // defect: its columns are squeezed under a button's own width), and the count
  // beside the list is the same as on main at 1280, 1320, 1340, 1366, 1440 and
  // 1536, and one more at 1400. Nothing is clipped that main showed, so it is
  // drawn beside the list from 860, which puts it there at 1366.
  { key: 'custom-fields', label: 'Custom fields', about: 'Your own fields on the vendor record', group: 'Organisation', cap: 'config.read', screen: 'custom-fields' },

  // Access and security
  { key: 'sso', label: 'Identity and SSO', about: 'Sign-in and single sign-on', group: 'Access and security', cap: 'config.read' },
  { key: 'people', label: 'People and roles', about: 'Who signs in, and their roles', group: 'Access and security', cap: 'user.administer', screen: 'users' },

  // Communications
  { key: 'email', label: 'Email', about: 'How mail is sent, and a test', group: 'Communications', cap: 'config.read' },
  { key: 'messaging', label: 'WhatsApp and SMS', about: 'Reaching vendors who do not answer email', group: 'Communications', cap: 'config.read' },

  // Data and integrations
  { key: 'integrations', label: 'Integrations', about: 'The systems this workspace connects to', group: 'Data and integrations', cap: 'config.read' },
  { key: 'registers', label: 'Risk registers', about: 'Connected risk registers and what they receive', group: 'Data and integrations', cap: 'config.read' },
  // FOCTTA Vendor Registry v1: where other systems read the register from and
  // where its changes go (interop_registry.go).
  { key: 'vendor-registry', label: 'Vendor registry', about: 'Where other systems read the register', group: 'Data and integrations', cap: 'config.read' },
  // Named lists of vendors the baseline rules and the bulk send choose by
  // (release 3.4, M7). Here rather than under Organisation, whose row of
  // entries is full at 1280.
  { key: 'vendor-groups', label: 'Vendor groups', about: 'Named lists of vendors for rules and sends', group: 'Data and integrations', cap: 'config.read' },

  // Platform
  // "Who is in charge" is a table of 818 px with its panel.
  { key: 'platform', label: 'Platform', about: 'The pairing with FOCTTA Consent', group: 'Platform', cap: 'config.read' },
  // Where the header's FOCTTA Consent and FOCTTA Discovery links point
  // (release 3.1, persona spec 7.4). The IT admin's and the TPRM lead's.
  { key: 'suite-links', label: 'Suite links', about: 'Where the Consent and Discovery links point', group: 'Platform', cap: 'config.read' },
]

export const GROUPS = ['Organisation', 'Access and security', 'Communications', 'Data and integrations', 'Platform']

// The console screen an entry is, for the persona's list (lib/screens.ts): a
// screen is its route, a pane is settings-<key>. Sign-in and the integrations
// are the IT admin's job and the tenant's name is everybody's, so Settings is
// filtered entry by entry rather than as one screen.
export function entryScreen(e: Entry): string {
  return e.screen ?? 'settings-' + e.key
}

export function entryAddress(e: Entry): string {
  return e.address ?? (e.screen ? '#/' + e.screen : '#/settings?t=' + e.key)
}

// The entry a route name is, where a screen is one.
export function entryOfScreen(name: string): Entry | undefined {
  return ENTRIES.find((e) => e.screen === name)
}

export function isSettingsScreen(name: string): boolean {
  return name === 'settings' || entryOfScreen(name) !== undefined
}

// What this reader may open: the capability the entry needs and a persona whose
// job it is. An entry the reader does not hold is not drawn.
export function shownEntries(): Entry[] {
  return ENTRIES.filter((e) => can(e.cap) && onScreen(entryScreen(e)))
}

// The pane the address names, as #/settings?t=<key> or #/settings/<key>. Empty
// when it names none, or names an entry that is a screen (those have routes).
export function paneFromHash(hash: string): string {
  const [path = '', q = ''] = hash.split('?')
  const t = new URLSearchParams(q).get('t') ?? path.replace(/^#\/?/, '').split('/')[1] ?? ''
  return t && ENTRIES.some((e) => e.key === t && !e.screen) ? t : ''
}

// The entry the page is on. A screen is its own entry. On #/settings itself it
// is the pane the address names; with none named it is no entry, and the
// route draws the index of every entry this reader holds (plan 3.9, 13 Oct
// 2026). Until then #/settings opened the first entry held, which left the
// person who came to find one setting reading Organisation, and the way back
// from an entry led to Organisation rather than to the list of settings.
export function currentEntry(screen: string, hash: string): Entry | undefined {
  const own = entryOfScreen(screen)
  if (own) return own
  const asked = paneFromHash(hash)
  return ENTRIES.find((e) => e.key === asked)
}

// The entries a search finds, in the index's order: every word typed is in the
// entry's name, its line or its group, anywhere in them. Nothing
// typed finds nothing (the index itself is the list of everything).
export function findEntries(query: string, shown: Entry[]): Entry[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) return []
  return shown.filter((e) => {
    const text = (e.label + ' ' + e.about + ' ' + e.group).toLowerCase()
    return words.every((w) => text.includes(w))
  })
}
